// SumOfAll.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { getToken, base_url } from "../../config/config";

// Helper function to convert hours to a formatted string (hours and minutes)
const convertHoursToTime = (hours) => {
  if (isNaN(hours)) return "0h 0m";
  const totalHours = Math.floor(hours);
  const minutes = Math.round((hours - totalHours) * 60);
  return `${totalHours}h ${minutes}m`;
};

// Helper function to calculate cost based on hours
const calculateCost = (hours) => {
  if (isNaN(hours)) return "0.00";
  return (hours * 60).toFixed(2); // Cost per hour is €60
};

// Enhanced helper function to parse "HH:MM:SS" to decimal hours
const parseTotalTimeToHours = (totalTime) => {
  if (!totalTime || typeof totalTime !== "string") {
    console.warn(`Invalid TotalTime format: ${totalTime}`);
    return 0;
  }

  const timeParts = totalTime.split(":").map(Number);

  // Check if all parts are valid numbers
  if (timeParts.length !== 3 || timeParts.some(isNaN)) {
    console.warn(`Invalid TotalTime format: ${totalTime}`);
    return 0;
  }

  const [hours, minutes, seconds] = timeParts;
  return hours + minutes / 60 + seconds / 3600;
};

const SumOfAll = () => {
  const dashboardRef = useRef(null);
  const token = getToken();

  // Define the API base URL from environment variables or fallback
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || `${base_url}/v1`;

  // --------------------------
  // 1. Total Material Cost (NETTO)
  // --------------------------
  const [sumSoll, setSumSoll] = useState(null);
  const [loadingSoll, setLoadingSoll] = useState(false);
  const [errorSoll, setErrorSoll] = useState(null);

  // Fetch Sum of "Soll" when component mounts
  useEffect(() => {
    const fetchSumSoll = async () => {
      setLoadingSoll(true);
      setErrorSoll(null);
      try {
        const response = await axios.get(`${API_BASE_URL}/sumOfallRechnungs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const sum = parseFloat(response.data.sumSoll);
        setSumSoll(isNaN(sum) ? 0 : sum);
      } catch (error) {
        console.error("Error fetching sum of Soll:", error);
        setErrorSoll(
          "Failed to fetch total material cost. Please try again later.",
        );
      } finally {
        setLoadingSoll(false);
      }
    };

    fetchSumSoll();
  }, [API_BASE_URL]);

  // --------------------------
  // 2. Total Hours Worked by Users
  // --------------------------
  const [hoursWorkedData, setHoursWorkedData] = useState([]);
  const [totalWorkedHours, setTotalWorkedHours] = useState(0);
  const [totalWorkedCost, setTotalWorkedCost] = useState(0);
  const [loadingHours, setLoadingHours] = useState(false);
  const [errorHours, setErrorHours] = useState(null);

  // Fetch Total Hours Worked by Users when component mounts
  useEffect(() => {
    const fetchHoursWorkedData = async () => {
      setLoadingHours(true);
      setErrorHours(null);
      try {
        const response = await axios.get(
          `http://35.159.43.224:3080/total-hours-by-user`, // need to update
        );
        const data = response.data;

        // Ensure data is an array
        if (!Array.isArray(data)) {
          throw new Error("Invalid data format received for hours worked.");
        }

        setHoursWorkedData(data);

        // Calculate total hours and cost
        const totalHours = data.reduce((sum, item) => {
          const hours = parseFloat(item.totalHours);
          return sum + (isNaN(hours) ? 0 : hours);
        }, 0);
        const totalCost = totalHours * 60; // Cost per hour is €60

        setTotalWorkedHours(totalHours);
        setTotalWorkedCost(isNaN(totalCost) ? 0 : totalCost);
      } catch (error) {
        console.error("Error fetching total hours worked data:", error);
        setErrorHours(
          "Failed to fetch user work hours. Please try again later.",
        );
      } finally {
        setLoadingHours(false);
      }
    };

    fetchHoursWorkedData();
  }, [API_BASE_URL]);

  // --------------------------
  // 3. Project Cost from July 25, 2024 Until Now
  // --------------------------
  const [totalProjectCost, setTotalProjectCost] = useState(0);
  const [loadingProjectCost, setLoadingProjectCost] = useState(false);
  const [errorProjectCost, setErrorProjectCost] = useState(null);

  // Function to handle project cost calculation
  const handleCalculateProjectCost = async () => {
    setLoadingProjectCost(true);
    setErrorProjectCost(null);
    setTotalProjectCost(0);

    try {
      const response = await axios.get(`${API_BASE_URL}/projectsFromJuly25`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;

      if (
        data.status === 1 &&
        Array.isArray(data.data) &&
        data.data.length > 0
      ) {
        // Calculate total project cost
        const totalCost = data.data.reduce((sum, project) => {
          const decimalHours = parseTotalTimeToHours(project.TotalTime);
          return sum + decimalHours * 60; // Cost per hour is €60
        }, 0);

        setTotalProjectCost(isNaN(totalCost) ? 0 : totalCost);
      } else {
        setErrorProjectCost("No projects found from July 25, 2024, until now.");
      }
    } catch (error) {
      console.error("Error fetching project cost data:", error);
      setErrorProjectCost(
        "Failed to fetch project costs. Please try again later.",
      );
    } finally {
      setLoadingProjectCost(false);
    }
  };

  // Automatically calculate project costs on component mount
  useEffect(() => {
    handleCalculateProjectCost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------------
  // 4. Total Gesamtpreis of All ExcelBulk Data
  // --------------------------
  const [totalGesamtpreis, setTotalGesamtpreis] = useState(0);
  const [loadingGesamtpreis, setLoadingGesamtpreis] = useState(false);
  const [errorGesamtpreis, setErrorGesamtpreis] = useState(null);

  // Function to fetch Gesamtpreis total
  const fetchGesamtpreisTotal = async () => {
    setLoadingGesamtpreis(true);
    setErrorGesamtpreis(null);
    setTotalGesamtpreis(0);
    try {
      const response = await axios.get(`${base_url}/v1/getExcelBulkTotal`);
      if (response.data.status === 1) {
        const total = parseFloat(response.data.totalGesamtpreis);
        setTotalGesamtpreis(isNaN(total) ? 0 : total);
      } else {
        setErrorGesamtpreis(
          response.data.message || "Failed to fetch Gesamtpreis total.",
        );
      }
    } catch (error) {
      console.error("Error fetching Gesamtpreis total:", error);
      setErrorGesamtpreis(
        "Failed to fetch Gesamtpreis total. Please try again later.",
      );
    } finally {
      setLoadingGesamtpreis(false);
    }
  };

  // Fetch Gesamtpreis total on component mount
  useEffect(() => {
    fetchGesamtpreisTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------------
  // 5. Compute Overall Total Cost
  // --------------------------
  const computeOverallTotal = () => {
    const materialCost = sumSoll || 0;
    const hoursCost = totalWorkedCost || 0;
    const projectCost = totalProjectCost || 0;
    return materialCost + hoursCost + projectCost;
  };

  const overallTotal = computeOverallTotal();

  // --------------------------
  // 6. Compute Profit and Profit/Loss Percentage
  // --------------------------
  const computeProfit = () => {
    return totalGesamtpreis - overallTotal;
  };

  const profit = computeProfit();

  const computeProfitLossPercentage = () => {
    if (overallTotal === 0) return 0;
    return (profit / overallTotal) * 100;
  };

  const profitLossPercentage = computeProfitLossPercentage();

  // --------------------------
  // 7. Handle PDF Export
  // --------------------------
  const handleExportPDF = () => {
    const input = dashboardRef.current;
    if (!input) {
      console.error("Dashboard reference is null");
      return;
    }

    html2canvas(input, { scale: 2 })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");

        // Calculate the number of pages
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save("dashboard_report.pdf");
      })
      .catch((error) => {
        console.error("Error exporting PDF:", error);
      });
  };

  return (
    <div style={styles.container} ref={dashboardRef}>
      <h1 style={styles.title}>Dashboard</h1>

      {/* --------------------------
          Export PDF Button
      -------------------------- */}
      <div style={styles.exportButtonContainer}>
        <button onClick={handleExportPDF} style={styles.exportButton}>
          Export Report as PDF
        </button>
      </div>

      {/* --------------------------
          1. Total Material Cost (NETTO)
      -------------------------- */}
      <div style={styles.section}>
        <h2>Total Material Cost (NETTO)</h2>
        {loadingSoll && <p>Calculating...</p>}
        {errorSoll && <p style={styles.error}>{errorSoll}</p>}
        {sumSoll !== null && !loadingSoll && !errorSoll && (
          <p style={styles.result}>
            {sumSoll.toLocaleString("de-DE", {
              style: "currency",
              currency: "EUR",
            })}
          </p>
        )}
      </div>

      {/* --------------------------
          2. Total Hours Worked by Users
      -------------------------- */}
      <div style={styles.section}>
        <h2>Total Hours Worked by Users (01-01-2024 till 25-07-2024)</h2>
        {loadingHours && <p>Loading data...</p>}
        {errorHours && <p style={styles.error}>{errorHours}</p>}
        {!loadingHours && !errorHours && (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              {/* Table Headers (optional) */}
              {/* <thead>
                <tr style={styles.tableHeader}>
                  <th>User</th>
                  <th>Total Hours Worked</th>
                  <th>Cost (€)</th>
                </tr>
              </thead> */}
              <tbody>
                {/* Table Rows (optional) */}
                {/* {hoursWorkedData.map((item, index) => (
                  <tr
                    key={index}
                    style={index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd}
                  >
                    <td>{item.user}</td>
                    <td>{convertHoursToTime(item.totalHours)}</td>
                    <td>{calculateCost(item.totalHours)}€</td>
                  </tr>
                ))} */}
                {/* Display Total */}
                <tr style={styles.tableFooter}>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td>
                    <strong>{convertHoursToTime(totalWorkedHours)}</strong>
                  </td>
                  <td>
                    <strong>{totalWorkedCost.toFixed(2)}€</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* --------------------------
          3. Project Cost from July 25, 2024 Until Now
      -------------------------- */}
      <div style={styles.section}>
        <h2>Project Cost from July 25, 2024 Until Now</h2>
        <button onClick={handleCalculateProjectCost} style={styles.button}>
          Refresh Project Costs
        </button>

        {/* Display Loading, Error, or Total Cost */}
        {loadingProjectCost && <p>Calculating project costs...</p>}
        {errorProjectCost && <p style={styles.error}>{errorProjectCost}</p>}
        {!loadingProjectCost && !errorProjectCost && (
          <div style={styles.projectCostResult}>
            <p style={styles.result}>
              Total Project Cost: {totalProjectCost.toFixed(2)}€
            </p>
          </div>
        )}
      </div>

      {/* --------------------------
          4. Total Gesamtpreis of All ExcelBulk Data
      -------------------------- */}
      <div style={styles.section}>
        <h2>Total Gesamtpreis of All Rechnungs (NATASHA)</h2>
        <button onClick={fetchGesamtpreisTotal} style={styles.button}>
          Refresh Gesamtpreis Total
        </button>

        {/* Display Loading, Error, or Total */}
        {loadingGesamtpreis && <p>Calculating total Gesamtpreis...</p>}
        {errorGesamtpreis && <p style={styles.error}>{errorGesamtpreis}</p>}
        {!loadingGesamtpreis && !errorGesamtpreis && (
          <div style={styles.gesamtpreisResult}>
            <p style={styles.result}>
              Total Gesamtpreis:{" "}
              {totalGesamtpreis.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
              })}
            </p>
          </div>
        )}
      </div>

      {/* --------------------------
          5. Overall Total Cost
      -------------------------- */}
      <div style={styles.section}>
        <h2>Overall Total Cost</h2>
        <div style={styles.overallTotalContainer}>
          <p style={styles.result}>
            {overallTotal.toLocaleString("de-DE", {
              style: "currency",
              currency: "EUR",
            })}
          </p>
        </div>
      </div>

      {/* --------------------------
          6. Profit and Profit/Loss Percentage
      -------------------------- */}
      <div style={styles.section}>
        <h2>Profit Analysis</h2>
        <div style={styles.profitContainer}>
          <p
            style={{
              ...styles.result,
              color: profit >= 0 ? "#28a745" : "#dc3545", // Green for profit, red for loss
            }}>
            Profit:{" "}
            {profit.toLocaleString("de-DE", {
              style: "currency",
              currency: "EUR",
            })}
          </p>
          <p
            style={{
              ...styles.result,
              color: profitLossPercentage >= 0 ? "#28a745" : "#dc3545", // Green for profit, red for loss
            }}>
            Profit/Loss Percentage: {profitLossPercentage.toFixed(2)}%
          </p>
        </div>
      </div>
    </div>
  );
};

// --------------------------
// Inline Styles
// --------------------------
const styles = {
  container: {
    maxWidth: "900px",
    margin: "40px auto",
    padding: "20px",
    fontFamily: "'Arial', sans-serif",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    boxShadow: "0 0 15px rgba(0, 0, 0, 0.2)",
  },
  title: {
    textAlign: "center",
    marginBottom: "20px", // Adjusted to accommodate export button
    color: "#333",
  },
  exportButtonContainer: {
    textAlign: "right",
    marginBottom: "20px",
  },
  exportButton: {
    padding: "10px 20px",
    backgroundColor: "#007bff",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
  },
  section: {
    marginBottom: "40px",
    padding: "20px",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
  },
  error: {
    color: "red",
  },
  result: {
    fontSize: "24px",
    fontWeight: "bold",
    marginTop: "10px",
  },
  tableContainer: {
    overflowX: "auto",
    marginTop: "20px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "600px",
  },
  tableHeader: {
    backgroundColor: "#007bff",
    color: "#ffffff",
    textAlign: "left",
    padding: "10px",
  },
  tableRowEven: {
    backgroundColor: "#f9f9f9",
  },
  tableRowOdd: {
    backgroundColor: "#ffffff",
  },
  tableFooter: {
    backgroundColor: "#f1f1f1",
    fontWeight: "bold",
  },
  button: {
    padding: "10px 20px",
    backgroundColor: "#28a745",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
    marginTop: "10px",
  },
  projectCostResult: {
    marginTop: "20px",
  },
  gesamtpreisResult: {
    marginTop: "20px",
  },
  overallTotalContainer: {
    marginTop: "20px",
    color: "#28a745",
  },
  profitContainer: {
    marginTop: "20px",
  },
};

export default SumOfAll;
